/**
 * RESIDENTIAL ELEVATOR body (2026-10-02, split out 2026-10-20 for the ELEVATOR RIDE round).
 *
 * One builder for the home lift, shared by the closed prototype (everything into one mesh,
 * the car at its shown stop) and the walkthrough's moving parts (the rails stay put; the car,
 * its button panel, each gate slat and each car button come out as their own pieces with the
 * car at the bottom stop, so the walk can raise the car and fold the gate). Both read ONE
 * layout, so the ridden car is byte-for-byte the drawn one.
 *
 * Local frame (objectKinds.ts): x across the width centred on 0, y from the back of the
 * hoistway (the rails) to the front (the landing), z up from the bottom stop's floor.
 */
import type { ParamValue } from '@aj/bim-kernel'
import { MeshBuilder, type MeshBuffers } from '../../extrude'
import { featureEdges } from '../../edges'
import type { SolidGroup } from '../opening/protos'

const PIT_FT = 8 / 12
const OVERRUN_FT = 1.5
/** the folding gate's slat pitch, and how tight the slats stack when it is folded open */
const SLAT_PITCH_FT = 0.22
const SLAT_STACK_FT = 0.045
/** the car operating panel's buttons: diameter, spacing, and the top button's height
 *  (48" is the highest a seated rider is expected to reach — ASME A17.1 5.3 / ADA) */
const BUTTON_R_FT = 0.065
const BUTTON_PITCH_FT = 0.26
const BUTTON_TOP_FT = 4.0
const DOOR_BUTTON_R_FT = 0.055

export interface ElevatorLayout {
  /** travel, bottom stop to top stop (ft) */
  T: number
  cabH: number
  /** the cab's interior footprint, local frame (inside the wall panels) */
  inner: { x0: number; x1: number; y0: number; y1: number }
  /** how many stops the car serves (one car button each) */
  stops: number
  /** each slat's x (closed) and where it parks when the gate folds open */
  slats: { x: number; open: number }[]
  /** each car button's centre (local, car at the bottom stop): button i = stop i */
  buttons: { x: number; y: number; z: number }[]
  /** the DOOR OPEN / DOOR CLOSE pair under the stop buttons (open on the left, as you face
   *  the panel) */
  doorButtons: { action: 'open' | 'close'; x: number; y: number; z: number }[]
}

export function elevatorLayout(p: Record<string, ParamValue>, w: number, d: number, h: number): ElevatorLayout {
  const T = Math.max(0, (p.elevatorTravelFt as number) ?? 10)
  const cabH = Math.max(6.5, h)
  const stops = Math.max(2, Math.round((p.elevatorStopCount as number) || 2))
  const cx0 = -w / 2 + 0.25, cx1 = w / 2 - 0.25
  const cy0 = 0.5, cy1 = d - 0.18
  const WT = 0.08
  const slats: { x: number; open: number }[] = []
  let i = 0
  for (let x = cx0 + WT + 0.08; x < cx1 - WT - 0.04; x += SLAT_PITCH_FT, i++) {
    slats.push({ x, open: cx0 + WT + 0.04 + i * SLAT_STACK_FT })
  }
  // the car operating panel: on the right-hand side panel, near the gate (where a rider
  // standing inside facing out reaches for it), top stop's button highest
  const faceX = cx1 - WT - 0.02
  const buttons = Array.from({ length: stops }, (_, s) => ({
    x: faceX, y: cy1 - 0.55, z: BUTTON_TOP_FT - (stops - 1 - s) * BUTTON_PITCH_FT,
  }))
  const dz = buttons[0].z - BUTTON_PITCH_FT
  const doorButtons = (['open', 'close'] as const).map((action, i) => ({ action, x: faceX, y: cy1 - 0.55 + (i ? -1 : 1) * 0.075, z: dz }))
  return { T, cabH, inner: { x0: cx0 + WT, x1: cx1 - WT, y0: cy0 + WT, y1: cy1 - 0.1 }, stops, slats, buttons, doorButtons }
}

/** where each kind of piece goes: the prototype routes everything into one mesh, the
 *  walkthrough gives the car, every slat and every button their own */
export interface ElevatorSinks {
  fixed: MeshBuilder
  car: MeshBuilder
  panel: MeshBuilder
  slat: (i: number) => MeshBuilder
  button: (i: number) => MeshBuilder
  /** the door open (0) / door close (1) buttons */
  doorButton: (i: number) => MeshBuilder
}

const quad = (b: MeshBuilder) => {
  const prism = (poly: [number, number][], zA: number, zB: number) => {
    const n = poly.length
    const bot = poly.map((q) => [q[0], q[1], zA])
    const top = poly.map((q) => [q[0], q[1], zB])
    for (let i = 2; i < n; i++) {
      b.addTri(top[0], top[i - 1], top[i], [0, 0, 1])
      b.addTri(bot[0], bot[i], bot[i - 1], [0, 0, -1])
    }
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      const nx = poly[j][1] - poly[i][1], ny = poly[i][0] - poly[j][0]
      const len = Math.hypot(nx, ny) || 1
      const nn = [nx / len, ny / len, 0]
      b.addTri(bot[i], bot[j], top[j], nn)
      b.addTri(bot[i], top[j], top[i], nn)
    }
  }
  const box = (x0: number, x1: number, y0: number, y1: number, zA: number, zB: number) => {
    if (x1 - x0 < 1e-4 || y1 - y0 < 1e-4 || zB - zA < 1e-4) return
    prism([[x0, y0], [x1, y0], [x1, y1], [x0, y1]], zA, zB)
  }
  /** a disc standing on the x = xa..xb face (axis along x) — a round push button */
  const cylX = (cy: number, cz: number, r: number, xa: number, xb: number, segs = 14) => {
    const pt = (x: number, a: number) => [x, cy + Math.cos(a) * r, cz + Math.sin(a) * r]
    for (let i = 0; i < segs; i++) {
      const a0 = (Math.PI * 2 * i) / segs, a1 = (Math.PI * 2 * (i + 1)) / segs, am = (a0 + a1) / 2
      const nrm = [0, Math.cos(am), Math.sin(am)]
      // wound outward (front faces out) — the walk's reticle only hits front faces
      b.addTri(pt(xa, a0), pt(xb, a1), pt(xb, a0), nrm)
      b.addTri(pt(xa, a0), pt(xa, a1), pt(xb, a1), nrm)
      b.addTri([xb, cy, cz], pt(xb, a0), pt(xb, a1), [1, 0, 0])
      b.addTri([xa, cy, cz], pt(xa, a1), pt(xa, a0), [-1, 0, 0])
    }
  }
  const ellipse = (cx: number, cy: number, rx: number, ry: number, segs = 16): [number, number][] =>
    Array.from({ length: segs }, (_, i) => {
      const a = (Math.PI * 2 * i) / segs
      return [cx + Math.cos(a) * rx, cy + Math.sin(a) * ry] as [number, number]
    })
  return { prism, box, cylX, ellipse }
}

/**
 * The elevator, emitted into `sinks` with the car at `cz` (feet above the bottom stop): two
 * T-section guide rails up the back of the hoistway (8" pit to 1'-6" above the top-stop cab)
 * with wall brackets; the carriage riding them; the cab — platform, wood-panel back and
 * sides with raised panel trim, ceiling with a round light, a handrail; the folding gate
 * across the open front with its head track; and (2026-10-20) the car operating panel, one
 * push button per stop, top stop highest.
 */
export function emitElevator(p: Record<string, ParamValue>, w: number, d: number, h: number, cz: number, sinks: ElevatorSinks): ElevatorLayout {
  const L = elevatorLayout(p, w, d, h)
  const { T, cabH } = L
  const fixed = quad(sinks.fixed), car = quad(sinks.car)
  // guide rails — T-section, flange against the back wall
  const rx = Math.min(1.1, w * 0.28)
  for (const sx of [-1, 1]) {
    const x = sx * rx
    fixed.box(x - 0.17, x + 0.17, 0, 0.06, -PIT_FT, T + cabH + OVERRUN_FT)        // flange
    fixed.box(x - 0.03, x + 0.03, 0.06, 0.3, -PIT_FT, T + cabH + OVERRUN_FT)      // blade
    for (let z = 0; z < T + cabH; z += 4) fixed.box(x - 0.2, x + 0.2, 0, 0.04, z + 1, z + 1.25)   // wall brackets
  }
  // carriage: the sling stiles on the rails, a crosshead and a plank under the platform
  for (const sx of [-1, 1]) car.box(sx * rx - 0.12, sx * rx + 0.12, 0.3, 0.46, cz - 0.4, cz + cabH + 0.25)
  car.box(-rx - 0.12, rx + 0.12, 0.3, 0.46, cz + cabH, cz + cabH + 0.25)
  car.box(-rx - 0.12, rx + 0.12, 0.3, 0.46, cz - 0.4, cz - 0.15)
  // the cab
  const cx0 = -w / 2 + 0.25, cx1 = w / 2 - 0.25
  const cy0 = 0.5, cy1 = d - 0.18
  const WT = 0.08
  car.box(cx0, cx1, cy0, cy1, cz - 0.33, cz)                           // platform
  car.box(cx0, cx1, cy0, cy0 + WT, cz, cz + cabH)                      // back panel
  car.box(cx0, cx0 + WT, cy0, cy1, cz, cz + cabH)                      // side panels
  car.box(cx1 - WT, cx1, cy0, cy1, cz, cz + cabH)
  car.box(cx0, cx1, cy0, cy1, cz + cabH, cz + cabH + 0.17)             // ceiling
  car.prism(car.ellipse(0, (cy0 + cy1) / 2, 0.35, 0.35), cz + cabH - 0.04, cz + cabH)  // light
  // raised panel trim on the back and sides (the residential read)
  for (const zz of [cz + 0.5, cz + 3.1]) {
    car.box(cx0 + 0.3, cx1 - 0.3, cy0 + WT, cy0 + WT + 0.03, zz, zz + 2.2)
    car.box(cx0 + WT, cx0 + WT + 0.03, cy0 + 0.3, cy1 - 0.3, zz, zz + 2.2)
    // the right side's lower front panel stops short of the button panel
    car.box(cx1 - WT - 0.03, cx1 - WT, cy0 + 0.3, cy1 - (zz < cz + 3 ? 0.3 : 1.05), zz, zz + 2.2)
  }
  // handrail on the back wall
  car.prism([[cx0 + 0.4, cy0 + WT + 0.14], [cx1 - 0.4, cy0 + WT + 0.14], [cx1 - 0.4, cy0 + WT + 0.22], [cx0 + 0.4, cy0 + WT + 0.22]], cz + 2.96, cz + 3.04)
  for (const xx of [cx0 + 0.5, cx1 - 0.5]) car.box(xx - 0.025, xx + 0.025, cy0 + WT, cy0 + WT + 0.18, cz + 2.97, cz + 3.03)
  // the folding (accordion) gate's head track rides the car; each slat is its own piece
  car.box(cx0 + WT, cx1 - WT, cy1 - 0.1, cy1, cz + cabH - 0.2, cz + cabH - 0.08)
  L.slats.forEach((s, i) => quad(sinks.slat(i)).box(s.x, s.x + 0.04, cy1 - 0.08, cy1 - 0.02, cz + 0.05, cz + cabH - 0.2))
  // the car operating panel — a brushed plate on the right side panel, a button per stop
  const panel = quad(sinks.panel)
  const top = L.buttons[L.buttons.length - 1], low = L.doorButtons[0]
  panel.box(cx1 - WT - 0.02, cx1 - WT, top.y - 0.17, top.y + 0.17, cz + low.z - 0.2, cz + top.z + 0.3)
  L.buttons.forEach((bt, i) => quad(sinks.button(i)).cylX(bt.y, cz + bt.z, BUTTON_R_FT, bt.x - 0.035, bt.x))
  L.doorButtons.forEach((bt, i) => quad(sinks.doorButton(i)).cylX(bt.y, cz + bt.z, DOOR_BUTTON_R_FT, bt.x - 0.035, bt.x))
  return L
}

/** the closed prototype: one mesh, the car at its shown stop; the panel reads metal and the
 *  buttons lit (the per-part colours ride OpeningProto.solidGroups) */
export function buildElevatorInto(p: Record<string, ParamValue>, w: number, d: number, h: number, b: MeshBuilder): SolidGroup[] {
  const T = Math.max(0, (p.elevatorTravelFt as number) ?? 10)
  const cz = p.elevatorCabAt === 'top' ? T : 0
  const panel = new MeshBuilder(), buttons = new MeshBuilder()
  emitElevator(p, w, d, h, cz, { fixed: b, car: b, panel, slat: () => b, button: () => buttons, doorButton: () => buttons })
  const groups: SolidGroup[] = [{ start: 0, count: b.idx.length, materialId: null }]
  for (const [src, look] of [[panel, 'metal'], [buttons, 'light']] as const) {
    const start = b.idx.length, off = b.vertCount
    b.pos.push(...src.pos)
    b.nrm.push(...src.nrm)
    for (const k of src.idx) b.idx.push(k + off)
    b.vertCount += src.vertCount
    groups.push({ start, count: b.idx.length - start, materialId: null, look })
  }
  return groups
}

/** one piece of the ridden elevator, local frame, car at the bottom stop */
export interface ElevatorAnimPiece {
  role: 'car' | 'slat' | 'button'
  /** a slat: how far it travels along x when the gate folds fully open */
  dx?: number
  /** a button: the stop it calls */
  stop?: number
  /** a door button: what it does */
  action?: 'open' | 'close'
  solid: MeshBuffers
  solidGroups?: SolidGroup[]
  edges: Float32Array
}

export interface ElevatorAnimParts {
  layout: ElevatorLayout
  fixed: { solid: MeshBuffers; edges: Float32Array }
  pieces: ElevatorAnimPiece[]
  solidMaterialId: string | null
}

/** the walkthrough's ELEVATOR RIDE parts (2026-10-20): the rails fixed, everything the car
 *  carries as its own piece, built with the car at the bottom stop (z = 0) */
export function buildElevatorAnimParts(typeParams: Record<string, ParamValue>): ElevatorAnimParts | null {
  if (typeParams.objectKind !== 'elevator') return null
  const p = typeParams
  const w = (p.widthFt as number) || 4, d = (p.depthFt as number) || 5, h = (p.heightFt as number) || 7
  const fixed = new MeshBuilder(), car = new MeshBuilder(), panel = new MeshBuilder()
  const slats: MeshBuilder[] = [], buttons: MeshBuilder[] = [], doorButtons: MeshBuilder[] = []
  const layout = emitElevator(p, w, d, h, 0, {
    fixed, car, panel,
    slat: (i) => (slats[i] ??= new MeshBuilder()),
    button: (i) => (buttons[i] ??= new MeshBuilder()),
    doorButton: (i) => (doorButtons[i] ??= new MeshBuilder()),
  })
  const piece = (b: MeshBuilder, extra: Omit<ElevatorAnimPiece, 'solid' | 'edges'>): ElevatorAnimPiece => {
    const solid = b.build()
    return { ...extra, solid, edges: featureEdges(solid) }
  }
  const pieces: ElevatorAnimPiece[] = [piece(car, { role: 'car' })]
  pieces.push(piece(panel, { role: 'car', solidGroups: [{ start: 0, count: panel.idx.length, materialId: null, look: 'metal' }] }))
  slats.forEach((b, i) => pieces.push(piece(b, { role: 'slat', dx: layout.slats[i].open - layout.slats[i].x })))
  buttons.forEach((b, i) => pieces.push(piece(b, { role: 'button', stop: i, solidGroups: [{ start: 0, count: b.idx.length, materialId: null, look: 'light' }] })))
  doorButtons.forEach((b, i) => pieces.push(piece(b, { role: 'button', action: layout.doorButtons[i].action, solidGroups: [{ start: 0, count: b.idx.length, materialId: null, look: 'light' }] })))
  const fs = fixed.build()
  const sm = p.surfaceMaterial
  return { layout, fixed: { solid: fs, edges: featureEdges(fs) }, pieces, solidMaterialId: typeof sm === 'string' && sm ? sm : null }
}
